import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/api";
import { toast } from "@/lib/toast";
import { Eye, EyeOff, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";


export default function PortalResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    axios.get(`${API}/portal/reset-password/${token}/validate`)
      .then(() => setStatus("valid"))
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/portal/reset-password`, { token, new_password: password });
      setStatus("done");
      toast.success("Mot de passe réinitialisé");
      setTimeout(() => navigate("/portal"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur — réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-[#072354] to-[#2877D1]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="portal-reset-card">
        <div className="px-6 py-7 text-white text-center bg-gradient-to-r from-[#072354] to-[#2877D1]">
          <div className="flex items-center justify-center gap-3 mb-1">
            <img src="/dg-logo.png" alt="DG Fiscal Flow" className="w-12 h-12 object-contain" />
            <div className="text-left">
              <h1 className="text-xl font-bold">DG Fiscal Flow</h1>
              <p className="text-xs text-blue-200">Espace client</p>
            </div>
          </div>
        </div>

        {status === "checking" && (
          <div className="p-10 text-center">
            <div className="w-6 h-6 border-2 border-blue-200 border-t-[#072354] rounded-full animate-spin mx-auto" />
          </div>
        )}

        {status === "invalid" && (
          <div className="p-6 space-y-4 text-center" data-testid="reset-invalid">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" />
            <h2 className="text-base font-semibold text-gray-700">Lien invalide ou expiré</h2>
            <p className="text-sm text-gray-500">Ce lien de réinitialisation n'est plus valide. Refaites une demande pour en recevoir un nouveau.</p>
            <Link to="/portal/forgot-password" className="inline-block text-sm text-white bg-[#072354] hover:bg-[#0a2d6e] px-4 py-2 rounded-lg" data-testid="reset-request-new-link">
              Refaire une demande
            </Link>
          </div>
        )}

        {status === "done" && (
          <div className="p-6 space-y-4 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
            <h2 className="text-base font-semibold text-gray-700">Mot de passe réinitialisé !</h2>
            <p className="text-sm text-gray-500">Vous allez être redirigé vers la page de connexion...</p>
          </div>
        )}

        {status === "valid" && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="portal-reset-form">
            <h2 className="text-base font-semibold text-gray-700 text-center">Choisissez un nouveau mot de passe</h2>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="reset-error">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
              <div className="relative">
                <input type={show ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#072354] focus:ring-2 focus:ring-[#072354]/20 pr-10 text-sm"
                  placeholder="Minimum 8 caractères" data-testid="reset-password-input" autoFocus />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmez le mot de passe</label>
              <input type={show ? "text" : "password"} value={confirm} onChange={(e) => { setConfirm(e.target.value); setError(""); }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#072354] focus:ring-2 focus:ring-[#072354]/20 text-sm"
                placeholder="Répétez le mot de passe" data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={loading || !password || !confirm}
              className="w-full text-white py-3 rounded-lg transition font-medium disabled:opacity-50 text-sm bg-[#072354] hover:bg-[#0a2d6e]"
              data-testid="reset-submit-btn">
              {loading ? "Réinitialisation..." : "Réinitialiser mon mot de passe"}
            </button>
            <div className="flex justify-center">
              <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:underline">
                <ArrowLeft className="w-4 h-4" /> Retour à la connexion
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
